import { Link } from 'react-router-dom'
import { HelpIcon } from '@/shared/components/HelpIcon'
import type { TenantAnalytics } from '@/lib/apiTypes'
import { formatCount, formatDuration } from '@/lib/format'
import { rowStyle } from '@/features/dashboard/cards/styles'

export function SlowestAppliesCard({ applies, orgSlug }: { applies: TenantAnalytics['slowest_applies']; orgSlug: string | null }) {
  return (
    <div className="card fu">
      <div className="card-header">
        <div>
          <div className="kicker">Performance</div>
          <h3 style={{ fontSize: '0.95rem' }}>Slowest applies</h3>
        </div>
        <HelpIcon track="help_slowest_applies" title="Slowest applies" kicker="// what this implies">
          <p>Workspaces ranked by the runtime of their <strong>most recent apply</strong>:
          the time from the state lock being taken to the new state being pushed - the
          plan and apply window of a single run. One row per workspace, so the list
          reads as where your pipeline stands today rather than its worst day ever.</p>
          <p>Runtime is also spend: an apply occupies a CI runner or agent for its full
          duration, so the workspaces at the top of this list consume the most
          runner time and account for an outsized share of your pipeline's
          <strong> infrastructure cost</strong>.</p>
          <p>A long tail usually points to a <strong>bloated or monolithic
          configuration</strong>. Splitting the workspace state into smaller modules and
          states cuts both apply time and blast radius - a single change touches fewer
          resources, finishes faster, and frees its runner sooner.</p>
        </HelpIcon>
      </div>
      {applies.map((s, i) => {
        // Ranked horizontal bars on a shared scale (the list arrives longest-first, so the
        // first row is the max). Single measure -> single hue; the duration figure is the
        // direct label, the bar carries the comparison.
        const maxMs = applies[0]?.duration_ms ?? 1
        const pct = Math.max((s.duration_ms / maxMs) * 100, 2)
        const body = (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.workspace_name}
              </div>
              <div style={{ fontFamily: 'var(--head)', fontSize: '0.9rem', color: 'var(--accent)', flexShrink: 0 }}>
                {formatDuration(s.duration_ms)}
              </div>
            </div>
            <div aria-hidden style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, margin: '7px 0 5px' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', borderRadius: 2 }} />
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text3)' }}>
              serial {s.serial} · {formatCount(s.resource_count)} resources
            </div>
          </div>
        )
        return orgSlug ? (
          <Link
            key={i}
            to={`/org/${orgSlug}/state/${encodeURIComponent(s.workspace_name)}`}
            style={rowStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg3)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '')}
          >
            {body}
          </Link>
        ) : (
          <div key={i} style={{ ...rowStyle, cursor: 'default' }}>
            {body}
          </div>
        )
      })}
      {applies.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">▲</div>
          <div>No apply runtime data yet</div>
        </div>
      )}
    </div>
  )
}

